// Ray-vs-panel pick with a slop margin around the panel edge. Returns a
// hit even when the ray crosses the panel's infinite plane just outside
// the visible mesh bounds, so the disc cursor stays drawn while its
// outer edge is still touching the panel. Within actual bounds, returns
// normalized UV so the caller can drive button picking; in the slop
// band, UV is null (laser past the visible edge → no button under it)
// but the cursor position is clamped onto the panel boundary.

import * as THREE from "three";

const _planeNormal = new THREE.Vector3();
const _toOrigin = new THREE.Vector3();
const _worldHit = new THREE.Vector3();
const _localHit = new THREE.Vector3();

export interface PanelPickHit {
  // World-space cursor position. Clamped to the panel boundary when the
  // hit fell into the slop band; the actual plane intersection when
  // inside the visible mesh.
  worldHit: THREE.Vector3;
  // Normalized (0..1, origin bottom-left) UV in panel-local space. Only
  // set when the ray landed inside the visible mesh; null in the slop
  // band so callers know there's no button under the cursor.
  uv: { x: number; y: number } | null;
  // Distance from `origin` to `worldHit`. Drives laser-length truncation.
  distance: number;
}

// `panel` must be a Mesh whose geometry is a PlaneGeometry (we read
// width/height from `geometry.parameters`). Local +Z faces outward —
// matches how palette/toolkit panels are set up.
export function intersectPanelWithSlop(
  panel: THREE.Mesh,
  slop: number,
  origin: THREE.Vector3,
  dir: THREE.Vector3,
): PanelPickHit | null {
  panel.updateMatrixWorld(true);
  // Panel's outward normal in world space (panel local +Z).
  _planeNormal.set(0, 0, 1).transformDirection(panel.matrixWorld);

  const denom = dir.dot(_planeNormal);
  if (Math.abs(denom) < 1e-8) return null;

  panel.getWorldPosition(_toOrigin);
  _toOrigin.sub(origin);
  const t = _toOrigin.dot(_planeNormal) / denom;
  if (t < 0) return null;

  _worldHit.copy(origin).addScaledVector(dir, t);
  _localHit.copy(_worldHit);
  panel.worldToLocal(_localHit);

  const geom = panel.geometry as THREE.PlaneGeometry;
  const halfW = geom.parameters.width / 2;
  const halfH = geom.parameters.height / 2;

  if (
    Math.abs(_localHit.x) > halfW + slop ||
    Math.abs(_localHit.y) > halfH + slop
  ) {
    return null;
  }

  const insideActual =
    Math.abs(_localHit.x) <= halfW && Math.abs(_localHit.y) <= halfH;

  if (insideActual) {
    return {
      worldHit: _worldHit.clone(),
      uv: {
        x: (_localHit.x + halfW) / (halfW * 2),
        y: (_localHit.y + halfH) / (halfH * 2),
      },
      distance: t,
    };
  }

  // Slop-only hit: clamp local position to the visible panel edge so
  // the cursor reads as flush against the boundary rather than floating
  // beyond it. Distance re-derived from the clamped point.
  _localHit.x = THREE.MathUtils.clamp(_localHit.x, -halfW, halfW);
  _localHit.y = THREE.MathUtils.clamp(_localHit.y, -halfH, halfH);
  _localHit.z = 0;
  panel.localToWorld(_localHit);
  return {
    worldHit: _localHit.clone(),
    uv: null,
    distance: origin.distanceTo(_localHit),
  };
}
